import { SignIn, SignedOut, useAuth, useClerk } from '@clerk/clerk-react';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState, useCallback, useRef } from 'react';
import { connect as connectWebSocket, disconnect as disconnectWebSocket } from '../../api/modules/realtime';
import { setSignInTokenCallback } from '../../api/modules/realtime/handlers/authMessage';
import { websocket, AuthMessageType } from '@playcastdotio/Messaging';
import { sendMessage } from '../../api/modules/realtime';
import { authStore } from '../../api/stores/authStore';
import { getPersistentPlayerId } from '../../metrics';
import { sendAnalytics } from '@playcastdotio/Analytics';

export const ElectronAuth = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { session, user } = useClerk();
  const [authStatus, setAuthStatus] = useState<'initial' | 'processing' | 'authenticated' | 'fallback'>('initial');
  const [hostAuthId, setHostAuthId] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [shouldShowDeepLink, setShouldShowDeepLink] = useState<boolean>(false);
  const [deepLinkUrl, setDeepLinkUrl] = useState<string>('');
  const fallbackTimeoutRef = useRef<NodeJS.Timeout>();
  const pollIntervalRef = useRef<NodeJS.Timeout>();
  const isAuthenticatedRef = useRef(false);
  const urlParams = new URLSearchParams(window.location.search);
  const forceError = urlParams.get('forceError') === 'true';

  // Helper function to check auth status
  const checkAuthStatus = useCallback(async () => {
    if (isAuthenticatedRef.current) return true;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const response = await fetch('http://localhost:52641/auth-status', {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      if (data.isAuthenticated && !isAuthenticatedRef.current) {
        isAuthenticatedRef.current = true;
        setAuthStatus('authenticated');
        setMessage('Successfully authenticated! You can close this window.');
        // Clear any existing timeouts/intervals
        if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to check auth status:', error);
      return false;
    }
  }, []);

  // Set up SSE connection for auth status updates
  useEffect(() => {
    let events: EventSource;

    try {
      events = new EventSource('http://localhost:52641/auth-events');

      events.addEventListener('error', () => {
        if (!isAuthenticatedRef.current) {
          console.warn('SSE connection failed - host application may not be running');
          events.close();
        }
      });

      events.addEventListener('auth_status', (event) => {
        const data = JSON.parse(event.data);
        if (isAuthenticatedRef.current) return;

        if (data.status === 'processing') {
          setAuthStatus('processing');
          setMessage('Processing authentication...');
        } else if (data.status === 'authenticated') {
          isAuthenticatedRef.current = true;
          setAuthStatus('authenticated');
          setMessage('Successfully authenticated! You can close this window.');
          // Clear any existing timeouts/intervals
          if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        }
      });

      checkAuthStatus();
    } catch (error) {
      console.warn('Failed to establish SSE connection:', error);
    }

    return () => {
      if (events) {
        events.close();
      }
      // Clean up any remaining timeouts/intervals
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [checkAuthStatus]);

  // Handle Clerk user signed in, to get id
  useEffect(() => {
    if (isSignedIn) {
      const userId = user?.id;
      userId && setHostAuthId(userId);
    }
  }, [isSignedIn, user]);

  // Handle Clerk authentication and WebSocket connection
  useEffect(() => {
    if (isSignedIn && !isAuthenticatedRef.current) {
      session?.getToken({ template: 'host-token' }).then((jwtToken) => {
        if (jwtToken) {
          // Store JWT token
          authStore.updateState({ jwtToken });

          // Set up token callback before connecting
          setSignInTokenCallback((token) => {
            if (!isAuthenticatedRef.current) {
              if (forceError) {
                alert('Forcing error - skipping local connection');
                console.warn('Forcing error - skipping local connection');
                setShouldShowDeepLink(true);
                initiateDeepLink(token);
                return;
              }

              // Try to send token to local host application
              fetch('http://localhost:52641/auth-callback?token=' + token, {
                signal: AbortSignal.timeout(5000),
              })
                .then(() => {
                  setAuthStatus('processing');
                  setMessage('Processing authentication...');
                })
                .catch((error) => {
                  console.warn('Failed to connect to local server:', error);
                  setShouldShowDeepLink(true);
                  initiateDeepLink(token);
                });
            }
          });

          // Connect to WebSocket with JWT
          connectWebSocket();

          // Send auth token request
          const msg = websocket.generators.auth(AuthMessageType.AUTH_SIGNIN_TOKEN, {});
          sendMessage(msg);
        }
      });
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isSignedIn, forceError]);

  // Helper function to initiate deep link fallback
  const initiateDeepLink = useCallback(
    (token: string) => {
      if (isAuthenticatedRef.current) return;

      setAuthStatus('fallback');
      setMessage('Launching Playcast application...');
      const deepLink = `playcast://auth?token=${token}`;
      setDeepLinkUrl(deepLink);
      window.location.href = deepLink;

      // Start polling for auth status
      pollIntervalRef.current = setInterval(async () => {
        const isAuthenticated = await checkAuthStatus();
        if (isAuthenticated) {
          clearInterval(pollIntervalRef.current);
        }
      }, 1000);

      // Set timeout to clear polling and show timeout message
      fallbackTimeoutRef.current = setTimeout(() => {
        clearInterval(pollIntervalRef.current);
        if (!isAuthenticatedRef.current) {
          setMessage('Unable to connect to Playcast.');
        }
      }, 10000);
    },
    [checkAuthStatus],
  );

  const renderContent = () => {
    if (!isLoaded) {
      return (
        <div className='flex flex-col gap-4 items-center py-20'>
          <p className='invisible text-xl'>Loading...</p>
          <FontAwesomeIcon className='mt-8 animate-spin size-10' icon={faSpinner} />
        </div>
      );
    }

    if (authStatus === 'authenticated') {
      if (hostAuthId) {
        sendAnalytics({
          name: 'player-host-link',
          role: 'host',
          persistentId: getPersistentPlayerId(),
          hostAuthId: hostAuthId,
        });
      }

      return (
        <div className='flex flex-col gap-4 items-center py-20'>
          <svg className='text-green-500 size-16' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
          </svg>
          <p className='text-xl'>{message}</p>
        </div>
      );
    }

    if (authStatus === 'fallback') {
      return (
        <div className='flex flex-col gap-4 items-center py-20'>
          <p className='text-xl'>{message}</p>
          <p className='text-sm text-gray-300'>If nothing happens, please ensure Playcast is installed and running.</p>
          {shouldShowDeepLink && (
            <p className='text-sm text-gray-300'>
              <a href={deepLinkUrl} className='underline'>
                Or click here to open Playcast
              </a>
            </p>
          )}
          <FontAwesomeIcon className='mt-8 animate-spin size-10' icon={faSpinner} />
        </div>
      );
    }

    if (authStatus === 'processing' || isSignedIn) {
      return (
        <div className='flex flex-col gap-4 items-center py-20'>
          <p className='text-xl'>{message}</p>
          <FontAwesomeIcon className='mt-8 animate-spin size-10' icon={faSpinner} />
        </div>
      );
    }

    return (
      <SignedOut>
        <SignIn forceRedirectUrl={'/host-login'} />
      </SignedOut>
    );
  };

  return (
    <div className='p-0 m-0 w-full h-auto bg-content text-content-text'>
      <div className='p-0 m-0 w-full h-auto bg-no-repeat bg-cover bg-hero bg-content text-content-text'>
        <div className='container flex flex-col gap-8 justify-center items-center px-8 py-32 mx-auto w-full h-auto'>{renderContent()}</div>
      </div>
    </div>
  );
};
